/* ═══════════════════════════════════════════════════════════════
   REVIEW — where a forma setting lives on the Designer's paper
   (SYNC_PLAN §3.2 "visual + list"). Pure: settings in, highlights out.

   zone settings light their BAND (header / footer / pre / post); level
   settings light every paragraph of that LEVEL; page and other settings
   have no single spot — their note sits at the top of the sheet.
   ═══════════════════════════════════════════════════════════════ */

import { parseFormaPath, type FormaChange } from '../../shared/sync';
import type { BandKind } from '../forma-studio';

export interface FormaReviewNote {
  label: string;
  /** Text-bearing (a zone's tokens): old struck, new added inline. Else a
   *  before → after swatch. */
  text: boolean;
  before: string;
  after: string;
  /** A conflict: what this project has now (shown plain). */
  oursText?: string;
  band?: BandKind;
  level?: number;
}

export interface FormaReviewHighlights {
  bands: BandKind[];
  levels: number[];
  current: FormaReviewNote | null;
}

const SPECIAL_POST = /^(endOfSectionText|eos|post)/;

/** Which band a setting path draws in (undefined = no single band). */
export function bandOfPath(path: string): BandKind | undefined {
  const p = parseFormaPath(path);
  if (!p) return undefined;
  const key = p.keys[0] === 'tokenStyles' ? (p.keys[1] ?? '') : p.keys[0];
  if (p.root === 'header_footer' || (p.root === 'special' && p.keys[0] === 'tokenStyles')) {
    if (key.startsWith('header')) return 'header';
    if (key.startsWith('footer')) return 'footer';
    if (key.startsWith('bodyTop')) return 'pre';
    if (key.startsWith('post')) return 'post';
    return undefined;
  }
  if (p.root === 'special') {
    if (key.startsWith('bodyTop')) return 'pre';
    if (SPECIAL_POST.test(key)) return 'post';
  }
  return undefined;
}

export function levelOfPath(path: string): number | undefined {
  const p = parseFormaPath(path);
  return p && p.root === 'levels' ? p.level : undefined;
}

export function formaReviewHighlights(
  items: FormaChange[],
  currentId: string | null
): FormaReviewHighlights {
  const bands = new Set<BandKind>();
  const levels = new Set<number>();
  let current: FormaReviewNote | null = null;
  for (const c of items) {
    const band = bandOfPath(c.path);
    const level = levelOfPath(c.path);
    if (band) bands.add(band);
    if (level != null) levels.add(level);
    if (c.id === currentId) {
      current = {
        label: c.label,
        text: c.settingKind === 'zone' && Array.isArray(c.value ?? c.baseValue),
        before: c.before,
        after: c.after,
        oursText: c.oursText,
        band,
        level,
      };
    }
  }
  return { bands: [...bands], levels: [...levels].sort((a, b) => a - b), current };
}
